import { ReactNode } from 'react';
import { Dropdown, Tooltip } from 'antd';
import { AiOutlineFile, AiOutlineHome, AiOutlineLogout, AiOutlineWarning, AiOutlineBank } from 'react-icons/ai';
import { BsBuilding, BsGearFill, BsReceipt, BsWhatsapp } from 'react-icons/bs';
import { HiChevronLeft, HiChevronRight, HiSelector } from 'react-icons/hi';
import { Link, useHistory, useLocation } from 'react-router-dom';

import { capitalize } from '../../../helpers/capitalize';
import { useAuth } from '../../../hooks/useAuth';
import { useDividasCount } from '../../../hooks/useDividasCount';
import { useSidebarCollapse } from '../../../hooks/useSidebarCollapse';
import { Logo } from '../../Common/Logo';
import { PulsingDot } from '../../Common/PulsingDot';
import {
  CollapseButton,
  CompanySelect,
  CompanySelectContainer,
  CompanySwitcherWrapper,
  CustomerName,
  LogoWrapper,
  LogoutButton,
  NavItem,
  NavSection,
  SidebarContainer,
  SidebarHeader,
  SIDEBAR_COLLAPSED_WIDTH,
  SIDEBAR_WIDTH,
  UserAvatar,
  UserInfo,
  UserName,
  UserSection,
  WhatsappButton,
  WhatsappContainer,
  WhatsappIconButton,
  WhatsappSection,
  WhatsappText,
} from './styles';

interface NavigationItem {
  key: string;
  label: string;
  icon: ReactNode;
  /** Texto do tooltip do ponto vermelho; sem texto, sem ponto. */
  alert?: string;
}

export const Sidebar = () => {
  const { isCollapsed, toggleCollapse } = useSidebarCollapse();
  const { user, signOut, signInByApiToken } = useAuth();
  const dividasCount = useDividasCount();
  const dividasAlert = dividasCount > 0
    ? `Você tem ${dividasCount} ${dividasCount === 1 ? 'pendência tributária' : 'pendências tributárias'}`
    : undefined;

  const navigationItems: NavigationItem[] = [
    { key: '/', label: 'Início', icon: <AiOutlineHome /> },
    { key: '/arquivos', label: 'Documentos', icon: <AiOutlineFile /> },
    { key: '/dividas-tributarias', label: 'Pendências Tributárias', icon: <AiOutlineWarning />, alert: dividasAlert },
    ...(user?.extrato_bancario ? [{ key: '/extrato-bancario', label: 'Extrato Bancário', icon: <AiOutlineBank /> }] : []),
    ...(user?.allow_nf ? [{ key: '/solicitacoes', label: 'Notas Fiscais', icon: <BsReceipt /> }] : []),
    { key: '/configuracoes', label: 'Configurações', icon: <BsGearFill /> },
  ];
  const location = useLocation();
  const history = useHistory();

  const getUserInitials = () => {
    if (!user?.name) return 'U';
    return user.name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const handleLogout = () => {
    signOut();
    history.push('/login');
  };

  const currentCompanyId = 'current';
  const companies = user?.id
    ? [
        { id: currentCompanyId, name: user.customer_name },
        ...(user.other_customers || []).map((c) => ({
          id: c.id.toString(),
          name: c.name,
          api_token: c.api_token,
        })),
      ]
    : [];

  const handleCompanyChange = (value: string) => {
    const company = companies.find((c) => c.id === value);
    if (company && 'api_token' in company && company.api_token) {
      signInByApiToken(company.api_token);
    }
  };

  return (
    <SidebarContainer
      $isCollapsed={isCollapsed}
      width={isCollapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_WIDTH}
      collapsed={isCollapsed}
      collapsedWidth={SIDEBAR_COLLAPSED_WIDTH}
    >
      <SidebarHeader $isCollapsed={isCollapsed}>
        {!isCollapsed && (
          <LogoWrapper $isCollapsed={false}>
            <Link to="/">
              <Logo tenant height={24} />
            </Link>
          </LogoWrapper>
        )}
        <Tooltip title={isCollapsed ? 'Expandir menu' : 'Recolher menu'} placement="right">
          <CollapseButton onClick={toggleCollapse} aria-label={isCollapsed ? 'Expandir menu' : 'Recolher menu'}>
            {isCollapsed ? <HiChevronRight /> : <HiChevronLeft />}
          </CollapseButton>
        </Tooltip>
      </SidebarHeader>

      {companies.length > 1 && (
        <CompanySwitcherWrapper $isCollapsed={isCollapsed}>
          <CompanySelectContainer>
            <span className="company-icon">
              <BsBuilding />
            </span>
            <CompanySelect
              value={currentCompanyId}
              onChange={(value) => handleCompanyChange(value as string)}
              suffixIcon={<HiSelector />}
            >
              {companies.map((company) => (
                <CompanySelect.Option key={company.id} value={company.id}>
                  {company.name}
                </CompanySelect.Option>
              ))}
            </CompanySelect>
          </CompanySelectContainer>
        </CompanySwitcherWrapper>
      )}

      <NavSection $isCollapsed={isCollapsed}>
        {navigationItems.map((item) =>
          isCollapsed ? (
            <Tooltip key={item.key} title={item.alert ? `${item.label}: ${item.alert.charAt(0).toLowerCase()}${item.alert.slice(1)}` : item.label} placement="right">
              <NavItem
                to={item.key}
                $isCollapsed={isCollapsed}
                $isActive={location.pathname === item.key}
              >
                <span style={{ position: 'relative', display: 'inline-flex' }}>
                  {item.icon}
                  {item.alert && <PulsingDot $floating />}
                </span>
              </NavItem>
            </Tooltip>
          ) : (
            <NavItem
              key={item.key}
              to={item.key}
              $isCollapsed={isCollapsed}
              $isActive={location.pathname === item.key}
            >
              {item.icon}
              <span>{item.label}</span>
              {item.alert && (
                <Tooltip title={item.alert} placement="right">
                  <PulsingDot />
                </Tooltip>
              )}
            </NavItem>
          )
        )}
      </NavSection>

      {user?.tenant_phones?.['2'] && (
        <WhatsappSection $isCollapsed={isCollapsed}>
          {isCollapsed ? (
            <Tooltip title="Falar no WhatsApp" placement="right">
              <WhatsappIconButton
                href={`https://wa.me/55${user.tenant_phones['2']}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <BsWhatsapp />
              </WhatsappIconButton>
            </Tooltip>
          ) : (
            <WhatsappContainer $isCollapsed={false}>
              <WhatsappText>Está com dúvidas? Fale com nossa equipe!</WhatsappText>
              <WhatsappButton
                href={`https://wa.me/55${user.tenant_phones['2']}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <BsWhatsapp />
                Falar no WhatsApp
              </WhatsappButton>
            </WhatsappContainer>
          )}
        </WhatsappSection>
      )}

      <UserSection $isCollapsed={isCollapsed}>
        {isCollapsed ? (
          <Dropdown
            menu={{
              items: [
                {
                  key: 'user',
                  label: (
                    <div style={{ padding: '4px 0' }}>
                      <div style={{ fontWeight: 600, color: 'rgba(0,0,0,0.85)' }}>
                        {user?.name || 'Usuário'}
                      </div>
                      <div style={{ fontSize: 12, color: 'rgba(0,0,0,0.45)' }}>
                        {capitalize(user?.customer_name || '')}
                      </div>
                    </div>
                  ),
                  disabled: true,
                },
                { type: 'divider' },
                {
                  key: 'logout',
                  label: 'Sair',
                  icon: <AiOutlineLogout />,
                  onClick: handleLogout,
                  danger: true,
                },
              ],
            }}
            placement="topRight"
            trigger={['click']}
          >
            <UserAvatar style={{ cursor: 'pointer' }}>{getUserInitials()}</UserAvatar>
          </Dropdown>
        ) : (
          <>
            <UserAvatar>{getUserInitials()}</UserAvatar>
            <UserInfo $isCollapsed={isCollapsed}>
              <UserName>{user?.name || 'Usuário'}</UserName>
              <CustomerName>{capitalize(user?.customer_name || '')}</CustomerName>
            </UserInfo>
            <Tooltip title="Sair" placement="right">
              <LogoutButton onClick={handleLogout} aria-label="Sair">
                <AiOutlineLogout />
              </LogoutButton>
            </Tooltip>
          </>
        )}
      </UserSection>
    </SidebarContainer>
  );
};
